import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Crown, ExternalLink, Users } from 'lucide-react'

import {
  useProject,
  useProjectMembers,
  type DeveloperGroup,
  type ProjectMember,
} from '@/hooks'

/**
 * 项目成员页面
 * 注意：项目成员现在从团队派生，此页面为只读显示
 * 成员管理请前往团队管理页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/$projectId/members')({
  component: ProjectMembersPage,
})

const DEVELOPER_GROUP_LABELS: Record<DeveloperGroup, string> = {
  product: '产品组',
  design: '美术组',
  frontend: '前端组',
  backend: '后端组',
  testing: '测试组',
}

const FUNCTION_ROLE_LABELS: Record<string, string> = {
  finance: '财务',
  hr: '人事',
  admin: '行政',
}

function ProjectMembersPage() {
  const params = Route.useParams()
  const projectId = params.projectId
  const navigate = useNavigate()

  const { data: project } = useProject(projectId)
  const { data: members, isLoading, error } = useProjectMembers(projectId)

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (error || !members) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">加载失败</div>
        <Link
          to="/pm/projects/$projectId"
          params={{ projectId }}
          className="text-sm text-primary hover:underline"
        >
          返回项目详情
        </Link>
      </div>
    )
  }

  const hasMembers =
    members.owner ||
    members.function.length > 0 ||
    Object.values(members.developers).some((g) => g.length > 0)

  return (
    <div className="space-y-6">
      {/* 返回和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => void navigate({ to: '/pm/projects/$projectId', params: { projectId } })}
          className="rounded-md p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">{project?.code}</p>
          <h1 className="text-2xl font-bold tracking-tight">项目成员</h1>
        </div>
        {project?.teamId && (
          <Link
            to="/pm/teams/$teamId"
            params={{ teamId: project.teamId }}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <ExternalLink className="h-4 w-4" />
            在团队中管理
          </Link>
        )}
      </div>

      {/* 提示信息 */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <p>
          项目成员现在从所属团队派生。如需添加或移除成员，请前往
          {project?.teamId ? (
            <Link
              to="/pm/teams/$teamId"
              params={{ teamId: project.teamId }}
              className="mx-1 font-medium underline hover:no-underline"
            >
              团队管理
            </Link>
          ) : (
            <span className="mx-1 font-medium">团队管理</span>
          )}
          页面操作。
        </p>
      </div>

      {/* 项目负责人 */}
      {members.owner && (
        <div className="rounded-lg border bg-card p-4">
          <h2 className="mb-4 font-semibold">项目负责人</h2>
          <MemberCard member={members.owner} isOwner />
        </div>
      )}

      {/* 职能成员 */}
      {members.function.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <h2 className="mb-4 font-semibold">职能成员</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {members.function.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                roleLabel={member.functionRole ? FUNCTION_ROLE_LABELS[member.functionRole] : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* 开发组成员 */}
      {(Object.keys(DEVELOPER_GROUP_LABELS) as DeveloperGroup[]).map((group) => {
        const groupMembers = members.developers[group] || []
        if (groupMembers.length === 0) {
          return null
        }

        return (
          <div key={group} className="rounded-lg border bg-card p-4">
            <h2 className="mb-4 font-semibold">{DEVELOPER_GROUP_LABELS[group]}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {groupMembers.map((member) => (
                <MemberCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        )
      })}

      {/* 空状态 */}
      {!hasMembers && (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <Users className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无成员</p>
            <p className="text-sm text-muted-foreground">
              {project?.teamId ? (
                <>
                  请前往
                  <Link
                    to="/pm/teams/$teamId"
                    params={{ teamId: project.teamId }}
                    className="mx-1 text-primary hover:underline"
                  >
                    团队管理
                  </Link>
                  添加成员
                </>
              ) : (
                '此项目尚未关联团队'
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

interface MemberCardProps {
  member: ProjectMember
  isOwner?: boolean
  roleLabel?: string
}

function MemberCard({ member, isOwner, roleLabel }: MemberCardProps) {
  const displayName = member.employee?.name || `员工 ${member.employeeId.slice(0, 8)}`
  const initials = member.employee?.name?.slice(0, 1) || member.employeeId.slice(0, 2).toUpperCase()

  return (
    <div className="flex items-center gap-3 rounded-md border bg-background p-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
        {initials}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{displayName}</span>
          {member.isLeader && (
            <Crown className="h-4 w-4 text-yellow-500" aria-label="组长" />
          )}
          {member.isPrimary && (
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
              主项目
            </span>
          )}
        </div>
        {roleLabel && (
          <span className="text-sm text-muted-foreground">{roleLabel}</span>
        )}
        {isOwner && (
          <span className="text-sm text-muted-foreground">负责人</span>
        )}
      </div>
    </div>
  )
}
